<template>
  <div
    id="register"
    class="authentication-container"
    :class="{'authentication-container-mobile': isMobileSize}"
  >

    <!-- LEFT SIDE -->

    <left-side v-if="!isMobileSize"/>

    <!-- RIGHT SIDE -->

    <div class="right-side" :class="{'mobile': isMobileSize}">

      <!-- TITLE -->

      <div class="title">
        <h3 class="title-header">Sign up</h3>
      </div>

      <!-- INPUTS -->

      <div class="content form" :class="{'mobile': isMobileSize}">
        <div class="inputs">

          <div class="input-container " v-if="!!member.email">
            <label
              for="email"
              class="label"
            >Email</label>

            <input
              type="text"
              id="email"
              class="primary-input"
              v-model.trim="$v.credentials.email.$model"
              :disabled="!!googleToken || !!member.email"
            >
          </div>

          <div class="input-container phone-number-input" v-if="!!member.phoneNumber">
            <label
              for="phoneNumber"
              class="label"
            >Phone Number</label>

            <MazPhoneNumberInput
              placeholder="Phone Number"
              noExample
              @update="phoneUpdated($event)"
              :default-country-code="credentials.countryCodeAlpha"
              :default-phone-number="credentials.phoneNumber"
              :disabled="googleToken || !!member.phoneNumber"
              class="phone"
              noValidation
            />
          </div>

          <div class="input-container" :class="{'mobile': isMobileSize}">
            <label
              for="firstName"
              class="label"
            >First Name</label>
            <input
              type="text"
              id="firstName"
              class="primary-input"
              v-model.trim="$v.credentials.firstName.$model"
              :disabled="googleToken"
              :class="[$v.credentials.firstName.$error ? 'error' : null,
                            $v.credentials.firstName.$dirty &&
                            !$v.credentials.firstName.$invalid ? 'success' : null]"
            >
            <validation-message
              :validation="$v.credentials.firstName"
              :metadata="memberMetadata.fields.firstName"
            />
          </div>

          <div class="input-container" :class="{'mobile': isMobileSize}">
            <label
              for="lastName"
              class="label"
            >{{ memberMetadata.fields.lastName.label }}</label>
            <input
              type="text"
              id="lastName"
              class="primary-input"
              v-model.trim="$v.credentials.lastName.$model"
              :disabled="googleToken"
              :class="[$v.credentials.lastName.$error ? 'error' : null,
                            $v.credentials.lastName.$dirty &&
                            !$v.credentials.lastName.$invalid ? 'success' : null]"
            >
            <validation-message
              :validation="$v.credentials.lastName"
              :metadata="memberMetadata.fields.lastName"
            />
          </div>

          <div class="input-container" :class="{'mobile': isMobileSize}">
            <label
              for="password"
              class="label"
            >{{ memberMetadata.fields.password.label }}</label>
            <input
              type="password"
              id="password"
              class="primary-input"
              v-model="password"
              :disabled="googleToken"
              :class="[$v.credentials.password.$error ? 'error' : null,
                            $v.credentials.password.$dirty &&
                            !$v.credentials.password.$invalid ? 'success' : null]"
            >
            <password-validation-message
              :validation="$v.credentials.password"
              :metadata="memberMetadata.fields.password"
              :newPassword="true"
            />
          </div>

          <div class="input-container" :class="{'mobile': isMobileSize}">
            <label
              for="confirmPassword"
              class="label"
            >Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              class="primary-input"
              v-model="$v.credentials.confirmPassword.$model"
              :disabled="googleToken"
              :class="[$v.credentials.confirmPassword.$error ? 'error' : null,
                            $v.credentials.confirmPassword.$dirty &&
                            !$v.credentials.confirmPassword.$invalid ? 'success' : null]"
            >
            <password-validation-message
              :validation="$v.credentials.confirmPassword"
              :confirmPassword="true"
            />
          </div>

          <!-- CHECKBOX -->

          <div class="checkbox-container">
            <input
              class="checkbox"
              id="privacyPolicy"
              type="checkbox"
              v-model="termsCheck"
            >
            <label
              class="check"
              for="privacyPolicy"
            ></label>
            <label
              class="label"
              for="privacyPolicy"
            >By signing up, you agree to our
              <router-link
                class="link"
                to=""
              >Terms & Conditions</router-link>
            </label>
          </div>
        </div>

        <!-- ACTIONS -->

        <div class="actions">
          <CoreButton
            @click.prevent="register"
            :disabled="isDisabled"
            :loading="isLoading"
          >
            Sign up
          </CoreButton>

          <CoreButton @click="registerWithGoogle" class="login-google">
            Register with Google
            <img
              src="@/assets/google-icon.png"
              alt="alt"
              width="18px"
            >
          </CoreButton>
        </div>
      </div>

      <!-- GO TO LOGIN -->

      <div class="go-to-login">
        <p>Already have an account?
          <router-link
            :to="{path: '/login', query: getQueries()}"
            class="link"
          >Log in</router-link>
        </p>
      </div>
    </div>

  </div>
</template>

<script>
import server, { createValidator } from './../server'
import { sameAs } from 'vuelidate/lib/validators'
import { mapMutations } from 'vuex'
import { mixin as clickout } from 'vue-clickout'
import { PandaService } from '../utils/request'
import { createToastNotification } from '../utils/toast'
import { GOOGLE_CLIENT_ID, GOOGLE_OAUTH_URL } from '../settings'
import { WaitStates } from '../utils/vuewait'
import { MazPhoneNumberInput } from 'maz-ui'
import { callingCountries } from 'country-data'

const CoreButton = () => import(
  /* webpackChunkName: "CoreButton" */ './../components/Core/CoreButton.vue'
)
const ValidationMessage = () => import(
  /* webpackChunkName: "ValidationMessage" */ './../components/ValidationMessage'
)
const PasswordValidationMessage = () => import(
  /* webpackChunkName: "PasswordValidationMessage" */ './../components/PasswordValidationMessage'
)
const LeftSide = () => import(
  /* webpackChunkName: "LeftSide" */ './../components/LeftSide'
)

export default {
  mixins: [clickout],
  name: 'Register',
  data () {
    return {
      password: null,
      credentials: {
        email: null,
        phoneNumber: '',
        countryCode: 98,
        countryCodeAlpha: 'BE',
        firstName: null,
        lastName: null,
        password: null,
        confirmPassword: null
      },
      memberMetadata: server.metadata,
      termsCheck: false,
      applicationId: this.$route.query.applicationId,
      state: this.$route.query.state,
      scopes: this.$route.query.scopes,
      token: this.$route.query.t_,
      redirectUri: this.$route.query.redirectUri,
      googleToken: this.$route.query.code,
      googleState: this.$route.query.state,
      member: null
    }
  },
  validations () {
    return {
      credentials: {
        email: createValidator(this.memberMetadata.fields.email),
        firstName: createValidator(this.memberMetadata.fields.firstName),
        lastName: createValidator(this.memberMetadata.fields.lastName),
        password: createValidator(this.memberMetadata.fields.password),
        confirmPassword: {
          sameAs: sameAs(() => this.password)
        }
      }
    }
  },
  computed: {
    query () {
      let queries = {}
      if (this.applicationId) {
        queries.applicationId = this.applicationId
      }
      if (this.state) {
        queries.state = this.state
      }
      if (this.redirectUri) {
        queries.redirectUri = this.redirectUri
      }
      if (this.scopes) {
        queries.scopes = this.scopes
      }
      if (this.token) {
        let member = atob(this.token.split('.')[1])
        let organizationId = JSON.parse(member).organizationId
        queries.organizationId = String(organizationId)
      }
      return queries
    },
    isDisabled () {
      const { firstName, lastName, confirmPassword } = this.credentials
      return !this.termsCheck || !this.password || !firstName || !lastName || !confirmPassword
    },
    isLoading () {
      return this.$wait.is(WaitStates.REGISTER_LOADING)
    },
    registerFormData () {
      if (this.googleToken) {
        return {
          oauthCode: this.googleToken,
          ownershipToken: JSON.parse(this.googleState).token,
          redirectUrl: window.location.href.split('?')[0]
        }
      }
      return {
        email: this.credentials.email,
        firstName: this.credentials.firstName,
        lastName: this.credentials.lastName,
        password: this.credentials.confirmPassword,
        ownershipToken: this.token
      }
    },
    isMobileSize () {
      return window.innerWidth <= 768
    },

    countriesData () {
      return callingCountries.all.map(country => {
        return {
          ...country,
          countryCallingCodes: country.countryCallingCodes[0]
        }
      })
    }
  },
  methods: {
    phoneUpdated (event) {
      this.credentials.countryCode = event.countryCallingCodes
      this.calcCountryCodeAlpha()
      this.credentials.phoneNumber = event.nationalNumber
    },

    calcCountryCodeAlpha (code) {
      let country = this.countriesData.find((country) => country.countryCallingCodes === `+${this.credentials.countryCode}`)
      country ? this.credentials.countryCodeAlpha = country.alpha2 : this.credentials.countryCodeAlpha = ''
      return country
    },

    registerWithGoogle () {
      window.location.href = `${GOOGLE_OAUTH_URL}?response_type=code&client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${window.location.protocol}//${window.location.hostname}%2Fregister&scope=email+profile&state=${this.stringifyRegisterData()}`
    },
    async register () {
      if (!this.googleToken) {
        this.$v.credentials.password.$model = this.password
        this.$v.credentials.$touch()
        if (this.$v.credentials.$invalid) return
      }

      this.$wait.start(WaitStates.REGISTER_LOADING)
      const { status, statusText, headers } = await PandaService({
        url: 'members',
        method: 'REGISTER',
        data: this.registerFormData
      })
      this.$wait.end(WaitStates.REGISTER_LOADING)

      if (status === 200) {
        createToastNotification(statusText, { type: 'success', position: 'bottom' })
        this.confirmIdentity()
        if (headers['x-new-jwt-token']) {
          server.token = headers['x-new-jwt-token']
        }
        if (this.applicationId) {
          this.$router.push({ path: '/permissions', query: this.query })
        } else {
          this.$router.push('/')
        }
      }
    },
    getQueries () {
      let queries = {}
      for (let query in this.$route.query) {
        queries[query] = this.$route.query[query]
      }
      if (Object.keys(queries).length) {
        return queries
      } else {
        return null
      }
    },
    stringifyRegisterData () {
      const registerData = {
        token: this.token,
        applicationId: this.applicationId,
        state: this.state,
        scopes: this.scopes,
        redirectUri: this.redirectUri,
        phoneNumber: this.credentials.phoneNumber,
        countryCode: this.credentials.countryCode
      }

      return JSON.stringify(registerData)
    },

    async handleRegisterWithGoogle () {
      const registerData = JSON.parse(this.googleState)
      this.applicationId = registerData.applicationId
      this.state = registerData.state
      this.scopes = registerData.scopes
      this.token = registerData.token
      this.redirectUri = registerData.redirectUri
      if (registerData.phoneNumber) {
        this.credentials.phoneNumber = registerData.phoneNumber
        this.credentials.countryCode = registerData.countryCode
        await this.calcCountryCodeAlpha(this.credentials.countryCode)
      }
      await this.register()
    },

    ...mapMutations([
      'confirmIdentity'
    ])
  },
  async beforeMount () {
    if (this.googleToken) {
      await this.handleRegisterWithGoogle()
    } else {
      this.member = JSON.parse(atob(this.token.split('.')[1]))
      if (this.member.phoneNumber) {
        this.credentials.phoneNumber = this.member.phoneNumber
        this.credentials.countryCode = this.member.countryCode
        await this.calcCountryCodeAlpha(this.credentials.countryCode)
      }
      this.credentials.email = this.member.email
    }
  },
  components: {
    LeftSide,
    ValidationMessage,
    PasswordValidationMessage,
    CoreButton,
    MazPhoneNumberInput
  }
}
</script>
